import React, { useState, useEffect } from 'react';
import { clientsAPI, visitsAPI, caregiverProfilesAPI, usersAPI } from '../services/api';
import { fmtClientTimeOnly, utcIsoToClientTzInput, clientTzInputToUtcIso } from '../services/timezone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  CalendarBlank, Clock, Plus, Trash, Repeat, Sparkle, CheckCircle, Play, CaretLeft, CaretRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, isSameMonth } from 'date-fns';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DAY_FULL = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const ClientScheduleTab = ({ clientId, clientTimezone, visits, userRole, onRefresh }) => {
  const [weeklySlots, setWeeklySlots] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSlotDialogOpen, setIsSlotDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [slotForm, setSlotForm] = useState({ day: 'monday', time: '09:00', duration_minutes: 60, caregiver_id: '', notes: '', pay_rate_override_enabled: false, pay_rate_override: '', bonus_amount: '', bonus_reason: '' });

  // Individual visit edit state
  const [isVisitEditOpen, setIsVisitEditOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitForm, setVisitForm] = useState({ scheduled_date: '', scheduled_time: '', duration_minutes: 60, caregiver_id: '', notes: '', pay_rate_override_enabled: false, pay_rate_override: '', bonus_amount: '', bonus_reason: '', check_in_time: '', check_out_time: '', change_reason: '' });
  const [savingVisit, setSavingVisit] = useState(false);

  useEffect(() => { loadData(); }, [clientId]);

  const loadData = async () => {
    try {
      const canManage = userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin';
      const [schedRes, cgRes, supRes] = await Promise.all([
        clientsAPI.getWeeklySchedule(clientId),
        canManage ? caregiverProfilesAPI.getAll() : Promise.resolve({ data: [] }),
        canManage ? usersAPI.getAll('supervisor') : Promise.resolve({ data: [] })
      ]);
      setWeeklySlots(schedRes.data.slots || []);
      setStartDate(schedRes.data.start_date || format(new Date(), 'yyyy-MM-dd'));
      setEndDate(schedRes.data.end_date || '');
      const activeProfiles = (cgRes.data || [])
        .filter(p => (p.status || 'active') !== 'inactive'
                  && (p.employment_status || 'active') === 'active');
      const cgDropdown = activeProfiles.map(p => ({
        id: p.user_id && p.user_id !== 'none' ? p.user_id : `__nologin__:${p.id}`,
        name: p.name,
        needs_login: !p.user_id || p.user_id === 'none',
        is_supervisor: false,
        profile_id: p.id,
      }));
      const cgUserIds = new Set(cgDropdown.filter(c => !c.needs_login).map(c => c.id));
      const supDropdown = (supRes.data || [])
        .filter(u => u.is_active !== false && !cgUserIds.has(u.id))
        .map(u => ({ id: u.id, name: u.name, needs_login: false, is_supervisor: true }));
      const merged = [...cgDropdown, ...supDropdown].sort((a, b) => a.name.localeCompare(b.name));
      setCaregivers(merged);
    } catch (error) { console.error('Error loading schedule:', error); }
    finally { setLoading(false); }
  };

  const openAddSlot = () => {
    setEditingIndex(null);
    setSlotForm({ day: 'monday', time: '09:00', duration_minutes: 60, caregiver_id: '', notes: '', pay_rate_override_enabled: false, pay_rate_override: '', bonus_amount: '', bonus_reason: '' });
    setIsSlotDialogOpen(true);
  };

  const openEditSlot = (slot, index) => {
    if (userRole !== 'supervisor' && userRole !== 'super_admin' && userRole !== 'admin') return;
    setEditingIndex(index);
    setSlotForm({
      day: slot.day || 'monday',
      time: slot.time || '09:00',
      duration_minutes: slot.duration_minutes || 60,
      caregiver_id: slot.caregiver_id || '',
      notes: slot.notes || '',
      pay_rate_override_enabled: slot.pay_rate_override != null,
      pay_rate_override: slot.pay_rate_override != null ? String(slot.pay_rate_override) : '',
      bonus_amount: slot.bonus_amount != null ? String(slot.bonus_amount) : '',
      bonus_reason: slot.bonus_reason || ''
    });
    setIsSlotDialogOpen(true);
  };

  const handleSaveSlot = () => {
    const payRate = slotForm.pay_rate_override_enabled && slotForm.pay_rate_override !== ''
      ? parseFloat(slotForm.pay_rate_override) : null;
    const bonus = slotForm.bonus_amount !== '' ? parseFloat(slotForm.bonus_amount) : null;
    const cleaned = {
      day: slotForm.day,
      time: slotForm.time,
      duration_minutes: slotForm.duration_minutes,
      caregiver_id: slotForm.caregiver_id,
      notes: slotForm.notes,
      pay_rate_override: payRate,
      bonus_amount: bonus,
      bonus_reason: bonus ? (slotForm.bonus_reason || null) : null,
    };
    if (editingIndex !== null) {
      const updated = weeklySlots.map((s, i) => i === editingIndex ? { ...s, ...cleaned } : s);
      setWeeklySlots(updated);
    } else {
      setWeeklySlots([...weeklySlots, { ...cleaned, id: `slot_${Date.now()}` }]);
    }
    setIsSlotDialogOpen(false);
  };

  const handleRemoveSlot = (index) => {
    setWeeklySlots(weeklySlots.filter((_, i) => i !== index));
  };

  const handleSaveSchedule = async () => {
    if (!startDate) { toast.error('Start date is required'); return; }
    if (endDate && endDate < startDate) { toast.error('End date must be after start date'); return; }
    setSaving(true);
    try {
      const res = await clientsAPI.updateWeeklySchedule(clientId, weeklySlots, startDate, endDate || null);
      const generated = res.data.visits_generated ?? 0;
      toast.success(`Schedule saved — ${generated} visit${generated !== 1 ? 's' : ''} generated`);
      if (onRefresh) onRefresh();
    } catch (error) { toast.error('Failed to save schedule'); }
    finally { setSaving(false); }
  };

  const getCaregiverName = (id) => caregivers.find(c => c.id === id)?.name || 'UNASSIGNED';

  const openEditVisit = (visit) => {
    if (userRole !== 'supervisor' && userRole !== 'super_admin' && userRole !== 'admin') return;
    setEditingVisit(visit);
    const tz = clientTimezone || 'America/New_York';
    setVisitForm({
      scheduled_date: visit.scheduled_date,
      scheduled_time: visit.scheduled_time,
      duration_minutes: visit.duration_minutes || 60,
      caregiver_id: visit.caregiver_id || '',
      notes: visit.notes || '',
      pay_rate_override_enabled: visit.pay_rate_override != null,
      pay_rate_override: visit.pay_rate_override != null ? String(visit.pay_rate_override) : '',
      bonus_amount: visit.bonus_amount != null ? String(visit.bonus_amount) : '',
      bonus_reason: visit.bonus_reason || '',
      check_in_time: visit.check_in_time ? utcIsoToClientTzInput(visit.check_in_time, tz) : '',
      check_out_time: visit.check_out_time ? utcIsoToClientTzInput(visit.check_out_time, tz) : '',
      change_reason: ''
    });
    setIsVisitEditOpen(true);
  };

  const openAddVisit = () => {
    if (userRole !== 'supervisor' && userRole !== 'super_admin' && userRole !== 'admin') return;
    setEditingVisit(null);
    setVisitForm({
      scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
      scheduled_time: '09:00',
      duration_minutes: 60,
      caregiver_id: '',
      notes: '',
      pay_rate_override_enabled: false,
      pay_rate_override: '',
      bonus_amount: '',
      bonus_reason: '',
      check_in_time: '',
      check_out_time: '',
      change_reason: ''
    });
    setIsVisitEditOpen(true);
  };

  const clockTimesChanged = () => {
    if (!editingVisit) return false;
    const tz = clientTimezone || 'America/New_York';
    const origIn = editingVisit.check_in_time ? utcIsoToClientTzInput(editingVisit.check_in_time, tz) : '';
    const origOut = editingVisit.check_out_time ? utcIsoToClientTzInput(editingVisit.check_out_time, tz) : '';
    return visitForm.check_in_time !== origIn || visitForm.check_out_time !== origOut;
  };

  const handleSaveVisit = async () => {
    if (!visitForm.scheduled_date || !visitForm.scheduled_time) {
      toast.error('Date and time are required');
      return;
    }
    if (clockTimesChanged()) {
      if (visitForm.check_out_time && !visitForm.check_in_time) { toast.error('Clock-out requires a clock-in time'); return; }
      if (visitForm.check_in_time && visitForm.check_out_time && new Date(visitForm.check_out_time) <= new Date(visitForm.check_in_time)) { toast.error('Clock-out must be after clock-in'); return; }
      if (!visitForm.change_reason.trim()) { toast.error('Please give a reason for changing clock times'); return; }
    }
    setSavingVisit(true);
    try {
      const payRate = visitForm.pay_rate_override_enabled && visitForm.pay_rate_override !== ''
        ? parseFloat(visitForm.pay_rate_override) : null;
      const bonus = visitForm.bonus_amount !== '' ? parseFloat(visitForm.bonus_amount) : null;
      if (editingVisit) {
        await visitsAPI.update(editingVisit.id, {
          client_id: editingVisit.client_id,
          caregiver_id: visitForm.caregiver_id || '',
          scheduled_date: visitForm.scheduled_date,
          scheduled_time: visitForm.scheduled_time,
          duration_minutes: parseInt(visitForm.duration_minutes) || 60,
          notes: visitForm.notes || null,
          pay_rate_override: payRate,
          bonus_amount: bonus,
          bonus_reason: bonus ? (visitForm.bonus_reason || null) : null,
        });
        if (clockTimesChanged()) {
          const tz = clientTimezone || 'America/New_York';
          await visitsAPI.updateTimes(editingVisit.id, {
            check_in_time: visitForm.check_in_time ? clientTzInputToUtcIso(visitForm.check_in_time, tz) : '',
            check_out_time: visitForm.check_out_time ? clientTzInputToUtcIso(visitForm.check_out_time, tz) : '',
            change_reason: visitForm.change_reason.trim(),
          });
        }
        toast.success('Visit updated');
      } else {
        await visitsAPI.create({
          client_id: clientId,
          caregiver_id: visitForm.caregiver_id || '',
          scheduled_date: visitForm.scheduled_date,
          scheduled_time: visitForm.scheduled_time,
          duration_minutes: parseInt(visitForm.duration_minutes) || 60,
          notes: visitForm.notes || null,
          pay_rate_override: payRate,
          bonus_amount: bonus,
          bonus_reason: bonus ? (visitForm.bonus_reason || null) : null,
        });
        toast.success('Visit added');
      }
      setIsVisitEditOpen(false);
      setEditingVisit(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save visit');
    } finally { setSavingVisit(false); }
  };

  const handleDeleteVisit = async (visit) => {
    if (!window.confirm(`Delete visit on ${visit.scheduled_date} at ${visit.scheduled_time}?`)) return;
    try {
      await visitsAPI.delete(visit.id);
      toast.success('Visit deleted');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete visit');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'scheduled': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  // Group visits by date for calendar display
  const visitsByDate = {};
  visits.forEach(v => {
    if (!visitsByDate[v.scheduled_date]) visitsByDate[v.scheduled_date] = [];
    visitsByDate[v.scheduled_date].push(v);
  });

  // Slots grouped by day
  const slotsByDay = {};
  DAYS.forEach(d => { slotsByDay[d] = weeklySlots.filter(s => s.day === d); });

  // Visits for selected date
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const selectedVisits = visitsByDate[selectedDateStr] || [];

  if (loading) return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6">
      {/* Master Weekly Schedule */}
      <Card className="rounded-2xl border border-border" data-testid="weekly-schedule-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Repeat weight="duotone" className="w-5 h-5 text-primary" />
                  Master Weekly Schedule
                </CardTitle>
                <CardDescription>Set recurring weekly visits — auto-generates visits for the date range below</CardDescription>
              </div>
              <div className="flex gap-2">
                {(userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin') && (
                  <>
                    <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={openAddSlot} data-testid="add-weekly-slot-btn">
                      <Plus weight="bold" className="w-3 h-3" /> Add Slot
                    </Button>
                    <Button size="sm" className="rounded-xl gap-1" onClick={handleSaveSchedule} disabled={saving} data-testid="save-weekly-schedule-btn">
                      {saving ? 'Saving...' : 'Save Schedule'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {(userRole === 'supervisor' || userRole === 'super_admin') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-muted/30 border border-border">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Schedule Start Date *</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" data-testid="schedule-start-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Schedule End Date <span className="text-muted-foreground/70 normal-case">(leave blank for ongoing)</span></Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl" data-testid="schedule-end-date" />
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {weeklySlots.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Repeat weight="duotone" className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No recurring schedule set</p>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {DAYS.map(day => (
                <div key={day} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-center text-muted-foreground">{DAY_LABELS[day]}</p>
                  {slotsByDay[day].length === 0 ? (
                    <div className="text-center py-3 text-xs text-muted-foreground/50 border border-dashed border-border rounded-xl">—</div>
                  ) : (
                    slotsByDay[day].map((slot, idx) => {
                      const slotIndex = weeklySlots.indexOf(slot);
                      const isUnassigned = !slot.caregiver_id;
                      const canEdit = userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin';
                      return (
                        <div
                          key={idx}
                          className={`p-2 rounded-xl border text-center relative group transition-all ${
                            isUnassigned
                              ? 'bg-red-50 border-red-400 hover:bg-red-100'
                              : 'bg-primary/10 border-primary/20 hover:bg-primary/15'
                          } ${canEdit ? 'cursor-pointer' : ''}`}
                          data-testid={`weekly-slot-${day}-${idx}`}
                          onClick={() => canEdit && openEditSlot(slot, slotIndex)}
                        >
                          <p className="text-xs font-medium">{slot.time}</p>
                          <p className="text-[10px] text-muted-foreground">{slot.duration_minutes}m</p>
                          <p className={`text-[10px] truncate font-medium ${
                            isUnassigned ? 'text-red-700' : 'text-muted-foreground'
                          }`}>
                            {isUnassigned ? 'UNASSIGNED' : getCaregiverName(slot.caregiver_id)}
                          </p>
                          {(slot.pay_rate_override != null || (slot.bonus_amount && slot.bonus_amount > 0)) && (
                            <p className="text-[9px] font-semibold text-amber-700 mt-0.5" title={`${slot.pay_rate_override != null ? `Override $${slot.pay_rate_override}/hr` : ''}${slot.bonus_amount ? ` +$${slot.bonus_amount} bonus` : ''}`}>
                              {slot.pay_rate_override != null && <>${slot.pay_rate_override}/hr</>}
                              {slot.pay_rate_override != null && slot.bonus_amount ? ' · ' : ''}
                              {slot.bonus_amount ? <>+${slot.bonus_amount}</> : ''}
                            </p>
                          )}
                          {canEdit && (
                            <button
                              className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full text-[8px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              onClick={(e) => { e.stopPropagation(); handleRemoveSlot(slotIndex); }}
                              data-testid={`remove-slot-${day}-${idx}`}
                            >
                              x
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Slot Dialog */}
      <Dialog open={isSlotDialogOpen} onOpenChange={setIsSlotDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingIndex !== null ? 'Edit Weekly Slot' : 'Add Weekly Time Slot'}</DialogTitle>
            <DialogDescription>{editingIndex !== null ? 'Update this recurring slot — changes apply to future visits on save' : 'Add a recurring visit slot to the weekly schedule'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Day of Week *</Label>
              <Select value={slotForm.day} onValueChange={(v) => setSlotForm({ ...slotForm, day: v })}>
                <SelectTrigger className="rounded-xl" data-testid="slot-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => <SelectItem key={d} value={d}>{DAY_FULL[d]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input type="time" value={slotForm.time} onChange={(e) => setSlotForm({ ...slotForm, time: e.target.value })} className="rounded-xl" data-testid="slot-time-input" />
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={String(slotForm.duration_minutes)} onValueChange={(v) => setSlotForm({ ...slotForm, duration_minutes: parseInt(v) })}>
                  <SelectTrigger className="rounded-xl" data-testid="slot-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                    <SelectItem value="240">4 hours</SelectItem>
                    <SelectItem value="300">5 hours</SelectItem>
                    <SelectItem value="360">6 hours</SelectItem>
                    <SelectItem value="420">7 hours</SelectItem>
                    <SelectItem value="480">8 hours</SelectItem>
                    <SelectItem value="540">9 hours</SelectItem>
                    <SelectItem value="600">10 hours</SelectItem>
                    <SelectItem value="660">11 hours</SelectItem>
                    <SelectItem value="720">12 hours</SelectItem>
                    <SelectItem value="780">13 hours</SelectItem>
                    <SelectItem value="840">14 hours</SelectItem>
                    <SelectItem value="900">15 hours</SelectItem>
                    <SelectItem value="960">16 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Caregiver (Optional)</Label>
              <Select value={slotForm.caregiver_id || 'none'} onValueChange={(v) => setSlotForm({ ...slotForm, caregiver_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="rounded-xl" data-testid="slot-caregiver-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {caregivers.map(cg => (
                    <SelectItem key={cg.id} value={cg.id} disabled={cg.needs_login}>
                      {cg.name}
                      {cg.is_supervisor && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">Supervisor</span>}
                      {cg.needs_login && <span className="ml-2 text-xs text-amber-600 font-medium">(needs login)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={slotForm.notes} onChange={(e) => setSlotForm({ ...slotForm, notes: e.target.value })} placeholder="Special instructions..." className="rounded-xl" data-testid="slot-notes-input" />
            </div>

            {/* Per-slot Caregiver Pay Override + Bonus */}
            <div className="rounded-xl border border-border p-3 space-y-3 bg-muted/20">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Caregiver Pay (applies to every visit generated from this slot)</p>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded"
                  checked={slotForm.pay_rate_override_enabled}
                  onChange={(e) => setSlotForm({ ...slotForm, pay_rate_override_enabled: e.target.checked, pay_rate_override: e.target.checked ? slotForm.pay_rate_override : '' })}
                  data-testid="slot-pay-override-toggle"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Override caregiver hourly rate</p>
                  <p className="text-xs text-muted-foreground">Off = default caregiver rate + OT rules. On = flat $/hr applied to every recurring visit from this slot.</p>
                </div>
              </label>
              {slotForm.pay_rate_override_enabled && (
                <div className="pl-7 space-y-1.5">
                  <Label className="text-xs">Custom rate ($/hr)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={slotForm.pay_rate_override}
                      onChange={(e) => setSlotForm({ ...slotForm, pay_rate_override: e.target.value })}
                      placeholder="25.00"
                      className="rounded-xl pl-6"
                      data-testid="slot-pay-override-amount"
                    />
                  </div>
                </div>
              )}
              <div className="border-t border-border pt-3 space-y-1.5">
                <Label className="text-xs">Bonus per visit (paid to caregiver, NOT billed)</Label>
                <div className="grid grid-cols-[130px_1fr] gap-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={slotForm.bonus_amount}
                      onChange={(e) => setSlotForm({ ...slotForm, bonus_amount: e.target.value })}
                      placeholder="0.00"
                      className="rounded-xl pl-6"
                      data-testid="slot-bonus-amount"
                    />
                  </div>
                  <Input
                    value={slotForm.bonus_reason}
                    onChange={(e) => setSlotForm({ ...slotForm, bonus_reason: e.target.value })}
                    placeholder="Reason (e.g. hard-to-staff overnight shift)"
                    className="rounded-xl"
                    disabled={!slotForm.bonus_amount || parseFloat(slotForm.bonus_amount) <= 0}
                    data-testid="slot-bonus-reason"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsSlotDialogOpen(false)}>Cancel</Button>
              <Button className="flex-1 rounded-xl" onClick={handleSaveSlot} data-testid="confirm-add-slot-btn">{editingIndex !== null ? 'Update Slot' : 'Add Slot'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Month Calendar (full grid) + Day Detail */}
      <Card className="rounded-2xl border border-border" data-testid="month-calendar-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarBlank weight="duotone" className="w-5 h-5" />
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-xl h-8 w-8 p-0" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="prev-month-btn"><CaretLeft weight="bold" className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" className="rounded-xl h-8 px-3" onClick={() => { const t = new Date(); setCurrentMonth(t); setSelectedDate(t); }} data-testid="today-btn">Today</Button>
              <Button size="sm" variant="outline" className="rounded-xl h-8 w-8 p-0" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="next-month-btn"><CaretRight weight="bold" className="w-4 h-4" /></Button>
            </div>
          </div>
          <CardDescription className="flex flex-wrap items-center gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span> Scheduled</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> In progress</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span> Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span> Unassigned / cancelled</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-center py-1">{d}</div>
            ))}
          </div>
          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1" data-testid="month-grid">
            {eachDayOfInterval({
              start: startOfWeek(startOfMonth(currentMonth)),
              end: endOfWeek(endOfMonth(currentMonth)),
            }).map(day => {
              const dStr = format(day, 'yyyy-MM-dd');
              const dayVisits = (visitsByDate[dStr] || []).slice().sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
              const inMonth = isSameMonth(day, currentMonth);
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={dStr}
                  type="button"
                  onClick={() => setSelectedDate(day)}
                  className={`relative text-left rounded-lg border min-h-[96px] md:min-h-[120px] p-1.5 md:p-2 transition-colors ${
                    isSelected ? 'border-primary ring-2 ring-primary/30 bg-primary/5' :
                    isToday ? 'border-primary/40 bg-primary/[0.03]' :
                    inMonth ? 'border-border bg-background hover:bg-muted/40' :
                    'border-transparent bg-muted/20 text-muted-foreground'
                  }`}
                  data-testid={`day-cell-${dStr}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs md:text-sm font-semibold ${isToday ? 'text-primary' : ''}`}>{format(day, 'd')}</span>
                    {dayVisits.length > 0 && (
                      <span className="text-[10px] font-mono bg-primary/10 text-primary rounded-full px-1.5 py-0.5">{dayVisits.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayVisits.slice(0, 3).map(v => {
                      const isUnassigned = !v.caregiver_id || v.caregiver_id === '' || v.caregiver_id === 'none';
                      const cgName = isUnassigned ? 'Unassigned' : (getCaregiverName(v.caregiver_id) || 'Unassigned');
                      const bg = isUnassigned ? 'bg-red-100 text-red-800 border-l-2 border-red-500' :
                                 v.status === 'completed' ? 'bg-green-100 text-green-900 border-l-2 border-green-500' :
                                 v.status === 'in_progress' ? 'bg-blue-100 text-blue-900 border-l-2 border-blue-500' :
                                 v.status === 'cancelled' ? 'bg-red-100 text-red-900 border-l-2 border-red-500' :
                                 'bg-yellow-100 text-yellow-900 border-l-2 border-yellow-500';
                      return (
                        <div
                          key={v.id}
                          className={`text-[10px] md:text-[11px] rounded px-1 py-0.5 truncate ${bg}`}
                          title={`${v.scheduled_time} — ${cgName} (${v.duration_minutes}m)`}
                          data-testid={`day-visit-chip-${v.id}`}
                        >
                          <span className="font-mono">{v.scheduled_time}</span> <span className="truncate">{cgName}</span>
                        </div>
                      );
                    })}
                    {dayVisits.length > 3 && (
                      <div className="text-[10px] text-muted-foreground pl-1">+{dayVisits.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day detail card */}
      <div className="grid grid-cols-1 gap-6">
        <Card className="rounded-2xl border border-border" data-testid="day-visits-card">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="font-heading text-lg">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </CardTitle>
                <CardDescription>
                  {selectedVisits.length} visit{selectedVisits.length !== 1 ? 's' : ''} scheduled
                </CardDescription>
              </div>
              {(userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin') && (
                <Button size="sm" className="rounded-xl gap-1 shrink-0" onClick={openAddVisit} data-testid="add-single-visit-btn">
                  <Plus weight="bold" className="w-3.5 h-3.5" /> Add Visit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedVisits.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarBlank weight="duotone" className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No visits on this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedVisits.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time)).map(visit => {
                  const isUnassigned = !visit.caregiver_id || visit.caregiver_id === '' || visit.caregiver_id === 'none';
                  const caregiverName = isUnassigned ? 'UNASSIGNED' : (getCaregiverName(visit.caregiver_id) || 'UNASSIGNED');
                  return (
                  <div
                    key={visit.id}
                    className={`p-4 rounded-xl transition-colors ${
                      isUnassigned ? 'bg-red-50 border-2 border-red-400 hover:bg-red-100' : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                    data-testid={`day-visit-${visit.id}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-10 rounded-full ${getStatusColor(visit.status)}`} />
                        <div>
                          <p className="font-medium text-sm">{visit.scheduled_time}</p>
                          <p className="text-xs text-muted-foreground">{visit.duration_minutes} min</p>
                          <p className={`text-xs mt-1 font-medium ${isUnassigned ? 'text-red-700' : 'text-muted-foreground'}`}>
                            {isUnassigned ? 'UNASSIGNED CAREGIVER' : `Caregiver: ${caregiverName}`}
                          </p>
                          {visit.check_in_time && (
                            <p className="text-xs text-muted-foreground mt-1">
                              In: {fmtClientTimeOnly(visit.check_in_time, clientTimezone)}
                              {visit.check_out_time && ` — Out: ${fmtClientTimeOnly(visit.check_out_time, clientTimezone)}`}
                            </p>
                          )}
                          {visit.notes && <p className="text-xs text-muted-foreground italic mt-1">"{visit.notes}"</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-full text-xs ${visit.status === 'completed' ? 'bg-green-100 text-green-800' : visit.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : visit.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {visit.status.replace('_', ' ')}
                        </Badge>
                        {(userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin') && visit.status !== 'cancelled' && (
                          <>
                            <Button size="sm" variant="ghost" className="rounded-xl h-8 px-2" onClick={() => openEditVisit(visit)} data-testid={`edit-visit-${visit.id}`}>
                              Edit
                            </Button>
                            {visit.status === 'scheduled' && (
                            <Button size="sm" variant="ghost" className="rounded-xl h-8 px-2 text-destructive hover:text-destructive" onClick={() => handleDeleteVisit(visit)} data-testid={`delete-visit-${visit.id}`}>
                              <Trash weight="bold" className="w-3.5 h-3.5" />
                            </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Visits List */}
      <Card className="rounded-2xl border border-border" data-testid="all-visits-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg">All Visits ({visits.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {visits.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">No visits scheduled</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {visits.sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date)).map(visit => {
                const isUnassigned = !visit.caregiver_id || visit.caregiver_id === '' || visit.caregiver_id === 'none';
                const caregiverName = isUnassigned ? 'UNASSIGNED' : (getCaregiverName(visit.caregiver_id) || 'UNASSIGNED');
                return (
                <div
                  key={visit.id}
                  className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                    isUnassigned ? 'bg-red-50 border-2 border-red-400 hover:bg-red-100' : 'bg-muted/20 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-8 rounded-full ${getStatusColor(visit.status)}`} />
                    <div>
                      <p className="text-sm font-medium">{visit.scheduled_date} at {visit.scheduled_time}</p>
                      <p className={`text-xs font-medium ${isUnassigned ? 'text-red-700' : 'text-muted-foreground'}`}>
                        {isUnassigned ? 'UNASSIGNED' : caregiverName} • {visit.duration_minutes} min
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-full text-xs ${visit.status === 'completed' ? 'bg-green-100 text-green-800' : visit.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : visit.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {visit.status.replace('_', ' ')}
                    </Badge>
                    {(userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin') && visit.status !== 'cancelled' && (
                      <>
                        <Button size="sm" variant="ghost" className="rounded-xl h-7 px-2 text-xs" onClick={() => openEditVisit(visit)} data-testid={`edit-all-visit-${visit.id}`}>
                          Edit
                        </Button>
                        {visit.status === 'scheduled' && (
                        <Button size="sm" variant="ghost" className="rounded-xl h-7 px-2 text-destructive hover:text-destructive" onClick={() => handleDeleteVisit(visit)} data-testid={`delete-all-visit-${visit.id}`}>
                          <Trash weight="bold" className="w-3 h-3" />
                        </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Individual Visit Dialog */}
      <Dialog open={isVisitEditOpen} onOpenChange={(open) => { if (!open) { setIsVisitEditOpen(false); setEditingVisit(null); } }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingVisit ? 'Edit Visit' : 'Add Visit'}</DialogTitle>
            <DialogDescription>{editingVisit ? 'Change caregiver, date, time, or notes for this single visit' : 'Create a one-off visit for this client (separate from the recurring weekly schedule)'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input type="date" value={visitForm.scheduled_date} onChange={(e) => setVisitForm({ ...visitForm, scheduled_date: e.target.value })} className="rounded-xl" data-testid="visit-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <Input type="time" value={visitForm.scheduled_time} onChange={(e) => setVisitForm({ ...visitForm, scheduled_time: e.target.value })} className="rounded-xl" data-testid="visit-time-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={String(visitForm.duration_minutes)} onValueChange={(v) => setVisitForm({ ...visitForm, duration_minutes: parseInt(v) })}>
                  <SelectTrigger className="rounded-xl" data-testid="visit-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                    <SelectItem value="240">4 hours</SelectItem>
                    <SelectItem value="300">5 hours</SelectItem>
                    <SelectItem value="360">6 hours</SelectItem>
                    <SelectItem value="420">7 hours</SelectItem>
                    <SelectItem value="480">8 hours</SelectItem>
                    <SelectItem value="540">9 hours</SelectItem>
                    <SelectItem value="600">10 hours</SelectItem>
                    <SelectItem value="660">11 hours</SelectItem>
                    <SelectItem value="720">12 hours</SelectItem>
                    <SelectItem value="780">13 hours</SelectItem>
                    <SelectItem value="840">14 hours</SelectItem>
                    <SelectItem value="900">15 hours</SelectItem>
                    <SelectItem value="960">16 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Caregiver</Label>
                <Select value={visitForm.caregiver_id || 'none'} onValueChange={(v) => setVisitForm({ ...visitForm, caregiver_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-xl" data-testid="visit-caregiver-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {caregivers.map(cg => (
                    <SelectItem key={cg.id} value={cg.id} disabled={cg.needs_login}>
                      {cg.name}
                      {cg.is_supervisor && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">Supervisor</span>}
                      {cg.needs_login && <span className="ml-2 text-xs text-amber-600 font-medium">(needs login)</span>}
                    </SelectItem>
                  ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={visitForm.notes} onChange={(e) => setVisitForm({ ...visitForm, notes: e.target.value })} placeholder="Visit notes..." className="rounded-xl" data-testid="visit-notes-input" />
              </div>

              {editingVisit && (
                <div className="rounded-xl border border-border p-3 space-y-3 bg-muted/20" data-testid="cst-clock-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Clock In / Out ({clientTimezone || 'America/New_York'})</Label>
                    <Badge variant="outline" className="rounded-full text-[10px] capitalize">{editingVisit.status.replace('_', ' ')}</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Clock In</Label>
                      <Input type="datetime-local" value={visitForm.check_in_time} onChange={(e) => setVisitForm({ ...visitForm, check_in_time: e.target.value })} className="rounded-xl" data-testid="cst-clock-in-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Clock Out</Label>
                      <Input type="datetime-local" value={visitForm.check_out_time} onChange={(e) => setVisitForm({ ...visitForm, check_out_time: e.target.value })} className="rounded-xl" data-testid="cst-clock-out-input" />
                    </div>
                  </div>
                  {visitForm.check_in_time && visitForm.check_out_time && new Date(visitForm.check_out_time) > new Date(visitForm.check_in_time) && (
                    <p className="text-xs text-muted-foreground">Worked: {((new Date(visitForm.check_out_time) - new Date(visitForm.check_in_time)) / 3600000).toFixed(2)} hrs — saving both times marks the visit <strong>completed</strong>; clock-in only marks it <strong>in progress</strong>.</p>
                  )}
                  {clockTimesChanged() && (
                    <div className="space-y-1">
                      <Label className="text-xs">Reason for change <span className="text-destructive">*</span></Label>
                      <Input value={visitForm.change_reason} onChange={(e) => setVisitForm({ ...visitForm, change_reason: e.target.value })} placeholder="e.g. Caregiver forgot to clock out" className="rounded-xl" data-testid="cst-clock-reason-input" />
                    </div>
                  )}
                  {(visitForm.check_in_time || visitForm.check_out_time) && (
                    <Button type="button" variant="ghost" size="sm" className="rounded-xl text-xs" onClick={() => setVisitForm({ ...visitForm, check_in_time: '', check_out_time: '' })} data-testid="cst-clock-clear">Clear clock times</Button>
                  )}
                </div>
              )}

              {/* Caregiver Pay Override + Bonus */}
              <div className="rounded-xl border border-border p-3 space-y-3 bg-muted/20">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Caregiver Pay (this visit only)</p>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded"
                    checked={visitForm.pay_rate_override_enabled}
                    onChange={(e) => setVisitForm({ ...visitForm, pay_rate_override_enabled: e.target.checked, pay_rate_override: e.target.checked ? visitForm.pay_rate_override : '' })}
                    data-testid="cst-visit-pay-override-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Override caregiver hourly rate</p>
                    <p className="text-xs text-muted-foreground">Off = default caregiver rate. On = flat $/hr for this visit.</p>
                  </div>
                </label>
                {visitForm.pay_rate_override_enabled && (
                  <div className="pl-7 space-y-1.5">
                    <Label className="text-xs">Custom rate ($/hr)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={visitForm.pay_rate_override}
                        onChange={(e) => setVisitForm({ ...visitForm, pay_rate_override: e.target.value })}
                        placeholder="25.00"
                        className="rounded-xl pl-6"
                        data-testid="cst-visit-pay-override-amount"
                      />
                    </div>
                  </div>
                )}
                <div className="border-t border-border pt-3 space-y-1.5">
                  <Label className="text-xs">Bonus (paid to caregiver, NOT billed)</Label>
                  <div className="grid grid-cols-[130px_1fr] gap-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={visitForm.bonus_amount}
                        onChange={(e) => setVisitForm({ ...visitForm, bonus_amount: e.target.value })}
                        placeholder="0.00"
                        className="rounded-xl pl-6"
                        data-testid="cst-visit-bonus-amount"
                      />
                    </div>
                    <Input
                      value={visitForm.bonus_reason}
                      onChange={(e) => setVisitForm({ ...visitForm, bonus_reason: e.target.value })}
                      placeholder="Reason (e.g. covered short-notice shift)"
                      className="rounded-xl"
                      disabled={!visitForm.bonus_amount || parseFloat(visitForm.bonus_amount) <= 0}
                      data-testid="cst-visit-bonus-reason"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsVisitEditOpen(false)}>Cancel</Button>
                <Button className="flex-1 rounded-xl" onClick={handleSaveVisit} disabled={savingVisit} data-testid="save-visit-btn">
                  {savingVisit ? 'Saving...' : (editingVisit ? 'Save Visit' : 'Add Visit')}
                </Button>
              </div>
            </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientScheduleTab;
